/**
 * Purpose: Render the compatibility "face" glyph (0–4 score) used by StarsBadge, the
 * compatibility legend/filter, and CompatibilityModal.
 * Inputs: `score` (0–4 → FACE_IMAGES index), `alt` (descriptive, translated by the caller;
 * pass "" when the face sits next to visible text), optional `id`, `size`, `priority`.
 * Outputs: A next/image img element; null for unknown scores. When `alt` is empty the image is
 * also `aria-hidden` so it is decorative per docs/accessibility.md §3.
 */
import Image from 'next/image'
import { FACE_IMAGES } from '@lib/face-images'
import './FaceImage.css'

interface FaceImageProps {
  id?: string
  score: number
  size?: number
  alt: string
  priority?: boolean
}

// Explicit switch (not an index lookup) so out-of-range scores return undefined → render null.
function getFaceImageSrc(score: number): string | undefined {
  switch (score) {
    case 0:
      return FACE_IMAGES[0]
    case 1:
      return FACE_IMAGES[1]
    case 2:
      return FACE_IMAGES[2]
    case 3:
      return FACE_IMAGES[3]
    case 4:
      return FACE_IMAGES[4]
    default:
      return undefined
  }
}

export function FaceImage({ id = 'face-image', score, size = 32, alt, priority = false }: FaceImageProps) {
  const src = getFaceImageSrc(score)
  if (!src) return null

  // Empty alt = the caller marked this face decorative (its meaning is in adjacent text), so
  // drop it from the accessibility tree entirely rather than leaving an unnamed graphic.
  const decorative = alt === ''

  return (
    <Image
      id={id}
      src={src}
      alt={alt}
      aria-hidden={decorative ? true : undefined}
      width={size}
      height={size}
      priority={priority}
      className="face-image"
    />
  )
}
